// theme.js — MUI theme built from the design tokens.
// Enforces the design-token system globally via component overrides so that
// un-migrated screens still inherit the correct radius, colors, and label styles.
import { createTheme } from '@mui/material/styles';
import { muiPalette, tokens } from './tokens';

const { neutral } = tokens.color;

const theme = createTheme({
  palette: {
    ...muiPalette,
    // Tonal surfaces for selected/active states and semantic banners.
    // (MUI's augmentColor preserves these extra keys.)
    primary: { ...muiPalette.primary, lighter: tokens.color.primarySurface },
    success: { ...muiPalette.success, lighter: tokens.color.successSurface },
    warning: { ...muiPalette.warning, lighter: tokens.color.warningSurface },
    error: { ...muiPalette.error, lighter: tokens.color.dangerSurface },
    info: { ...muiPalette.info, lighter: tokens.color.infoSurface },
    text: {
      primary: neutral[900],
      secondary: neutral[500],
    },
    divider: neutral[200],
    background: {
      default: neutral[50],
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: tokens.typography.fontFamily,
    button: {
      textTransform: 'none',
      fontWeight: tokens.typography.weights.semibold,
    },
  },
  // Default radius = control radius (6px). Surfaces override to 10px below.
  shape: {
    borderRadius: 6,
  },
  // Spacing unit stays at 8px: sx spacing values 1, 2, 3, 4 => 8, 16, 24, 32px.
  spacing: 8,
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
  components: {
    // ------------------------------------------------------------------
    // Buttons: one primary (solid blue), one secondary (white + gray-300
    // border), one danger (solid red). 6px radius, no pill shapes.
    // ------------------------------------------------------------------
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: tokens.radius.control,
          minHeight: '40px',
          padding: '8px 16px',
          boxShadow: 'none',
          '@media (max-width:600px)': {
            fontSize: '0.875rem',
          },
        },
        outlined: {
          borderColor: neutral[300],
          color: neutral[700],
          backgroundColor: '#ffffff',
          '&:hover': {
            backgroundColor: neutral[50],
            borderColor: neutral[400],
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: tokens.radius.control,
          '@media (max-width:600px)': {
            padding: '8px',
          },
        },
      },
    },
    // ------------------------------------------------------------------
    // Inputs: 6px radius, gray-300 border, no legend-on-border label style
    // (labels are rendered above inputs via the FormField component).
    // ------------------------------------------------------------------
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: tokens.radius.control,
          backgroundColor: '#ffffff',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: neutral[300],
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: neutral[400],
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: tokens.color.primary,
            borderWidth: '1px',
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: tokens.typography.label.fontSize,
          fontWeight: tokens.typography.label.fontWeight,
          color: neutral[700],
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontSize: tokens.typography.label.fontSize,
          fontWeight: tokens.typography.label.fontWeight,
          color: neutral[700],
          '&.Mui-focused': {
            color: neutral[700],
          },
        },
        asterisk: {
          color: tokens.color.danger,
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          marginLeft: 0,
          marginRight: 0,
        },
      },
    },
    // ------------------------------------------------------------------
    // Surfaces: 10px radius for cards, papers, dialogs, menus stay 6px.
    // ------------------------------------------------------------------
    MuiPaper: {
      styleOverrides: {
        rounded: {
          borderRadius: tokens.radius.surface,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: tokens.radius.surface,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: tokens.radius.surface,
          '@media (max-width:600px)': {
            margin: '16px',
            width: 'calc(100% - 32px)',
            maxHeight: 'calc(100% - 32px)',
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: tokens.radius.control,
        },
      },
    },
    // ------------------------------------------------------------------
    // Chips: 6px radius, 500 weight. Neutral "default" chip styling.
    // ------------------------------------------------------------------
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: tokens.radius.control,
          fontWeight: tokens.typography.weights.medium,
        },
      },
    },
    // ------------------------------------------------------------------
    // Alerts: semantic surfaces only. Info alerts are neutral gray, never blue.
    // ------------------------------------------------------------------
    MuiAlert: {
      styleOverrides: {
        standardInfo: {
          backgroundColor: tokens.color.infoSurface,
          color: neutral[800],
          '& .MuiAlert-icon': { color: neutral[500] },
        },
        standardSuccess: {
          backgroundColor: tokens.color.successSurface,
          color: tokens.color.success,
          '& .MuiAlert-icon': { color: tokens.color.success },
        },
        standardWarning: {
          backgroundColor: tokens.color.warningSurface,
          color: tokens.color.warning,
          '& .MuiAlert-icon': { color: tokens.color.warning },
        },
        standardError: {
          backgroundColor: tokens.color.dangerSurface,
          color: tokens.color.danger,
          '& .MuiAlert-icon': { color: tokens.color.danger },
        },
      },
    },
    // ------------------------------------------------------------------
    // Tables: neutral header treatment (replaces ad-hoc grey.100/bold styles).
    // ------------------------------------------------------------------
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: neutral[200],
          '@media (max-width:600px)': {
            padding: '8px',
            fontSize: '0.875rem',
          },
        },
        head: {
          backgroundColor: neutral[50],
          color: neutral[600],
          fontSize: '12px',
          fontWeight: tokens.typography.weights.semibold,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: '16px',
          fontWeight: tokens.typography.weights.semibold,
          color: neutral[900],
        },
      },
    },
  },
});

export default theme;
